import { PartialSanityImageType } from "@silversea-ssc/ui-components-react";
import { CruiseCardModalModalDataQuery } from "types/graphql-types";
import { useStaticCruiseCard } from "features/cruise/use-static-cruise-card";

export type KeepBookingCruiseContent = {
  cardImage: PartialSanityImageType;
  data: {
    cruiseCode: string;
    departureDate: string;
    departurePort:
      | {
          name: { localized: string };
        }
      | undefined;
    arrivalDate: string;
    arrivalPort:
      | {
          name: { localized: string };
        }
      | undefined;
    days: number;
    suiteName?: {
      localized?: string;
    };
  };
};

/**
 * Loads the keep-booking banner content from the static cruise-card modal
 * page-data instead of querying Sanity live. A hidden cruise has no modal
 * (404) so the banner is simply not shown, matching the live query's
 * `isVisible == true` filter. The suite name is matched on
 * `configurations[].code` (the live query matched a `codes` field that no
 * shipVersion document has, so it never resolved).
 */
export const useKeepBookingContent = (
  cruiseCode?: string,
  suiteCategoryCode?: string
): {
  isLoading: boolean;
  isError: boolean;
  status: string;
  data: KeepBookingCruiseContent | undefined;
} => {
  const { isLoading, isError, status, data } = useStaticCruiseCard(cruiseCode);
  return {
    isLoading,
    isError,
    status,
    data: data ? toContent(data, suiteCategoryCode) : undefined
  };
};

const toContent = (
  pageData: CruiseCardModalModalDataQuery,
  suiteCategoryCode?: string
): KeepBookingCruiseContent | undefined => {
  const { cruise, specialVoyage } = pageData;
  const voyage = cruise || specialVoyage;
  const data = voyage?.data;
  const cardImage = cruise?.cardImage ?? specialVoyage?.card?.image;
  if (!data?.cruiseCode || !cardImage) return undefined;

  const suite = suiteCategoryCode
    ? data.shipVersion?.suites?.find((s) =>
        (s?.configurations || []).some((c) => c?.code === suiteCategoryCode)
      )
    : undefined;

  return {
    cardImage: cardImage as PartialSanityImageType,
    data: {
      cruiseCode: data.cruiseCode,
      departureDate: data.departureDate ?? "",
      departurePort: data.departurePort?.name?.localized
        ? { name: { localized: data.departurePort.name.localized } }
        : undefined,
      arrivalDate: data.arrivalDate ?? "",
      arrivalPort: data.arrivalPort?.name?.localized
        ? { name: { localized: data.arrivalPort.name.localized } }
        : undefined,
      days: data.days ?? 0,
      suiteName: suite?.category?.name?.localized
        ? { localized: suite.category.name.localized }
        : undefined
    }
  };
};
